import * as R from 'ramda'
import React from 'react'
import { Button, List, Message, Modal } from 'semantic-ui-react'
import { Card, CardContent, CardGroup } from 'components/Card'
import styled from 'styled-components'

import { monthlyAmount, priceAmount } from 'libs/billing/price'
import { isLegacyPlan } from 'libs/billing/legacy-plan'

const CenteredDivContent = styled.div`
  text-align: center;
`

const PLAN_NICKNAME_DISPLAY_ORDER = ['starter', 'pro', 'elite', 'premium', 'scale']

function planDisplayRank(plan) {
  const nickname = String(R.prop('nickname', plan) ?? '').toLowerCase()
  const rank = PLAN_NICKNAME_DISPLAY_ORDER.findIndex((name) => nickname.includes(name))

  return rank === -1 ? PLAN_NICKNAME_DISPLAY_ORDER.length : rank
}

const SELECTABLE_PLAN_NICKNAMES = ['starter', 'pro', 'premium', 'scale']

function isSelectablePlan(plan) {
  const nickname = String(R.prop('nickname', plan) ?? '').toLowerCase()

  return isLegacyPlan(plan) || SELECTABLE_PLAN_NICKNAMES.some((name) => nickname.includes(name))
}

function displayedPlans(plans) {
  return R.sortBy(planDisplayRank, plans.filter(isSelectablePlan))
}

/**
 * @params {object} props
 * @params {object} props.userSubscription
 * @params {function} props.createOnChoosePlanButtonClicked
 * @params {boolean} props.isModalOpened
 * @params {boolean} props.isLegacySubscription
 * @params {function} props.onChangeType
 * @params {function} props.onModalClosed
 * @params {array} props.plan
 * @params {string} props.type
 */
export const PlanDetailModal = ({
  createOnChoosePlanButtonClicked,
  userSubscription,
  isModalOpened,
  isLegacySubscription,
  onChangeType,
  onModalClosed,
  plans,
  type,
  enableNewBillingPlan,
  enableNewPlanCardUI,
}) => {
  const visiblePlans = enableNewPlanCardUI ? displayedPlans(plans) : plans
  const isExpandedGrid = enableNewPlanCardUI && visiblePlans.length > 3

  return (
    <Modal open={isModalOpened} onClose={onModalClosed} size={isExpandedGrid ? 'large' : undefined}>
      <Modal.Header>
        <h1 className="ui center aligned header">Choose your plan</h1>
      </Modal.Header>
      <Modal.Content>
        {isLegacySubscription && (
          <Message
            header="You're on a legacy plan"
            content={
              <React.Fragment>
                Please <a href="mailto:support@simplesat.io">contact Simplesat support</a> to make
                changes to your existing plan.
              </React.Fragment>
            }
          />
        )}
        <CenteredDivContent>
          <Button.Group basic>
            <Button active={type === 'month'} onClick={() => onChangeType('month')}>
              Monthly
            </Button>
            <Button active={type === 'year'} onClick={() => onChangeType('year')}>
              Yearly
            </Button>
          </Button.Group>
        </CenteredDivContent>
        <CardGroup itemsPerRow={isExpandedGrid ? 4 : 3} stackable centered className="!my-3">
          {visiblePlans.map((plan) => {
            const isCurrentPlan =
              !['canceled', 'incomplete_expired'].includes(R.path(['status'], userSubscription)) &&
              R.path(['plan', 'id'], userSubscription) === plan.id

            const onChoosePlanButtonClicked = createOnChoosePlanButtonClicked(plan)
            const buttonDisabled = isCurrentPlan
            return (
              <PlanCard
                key={plan.id}
                plan={plan}
                buttonDisabled={buttonDisabled}
                onChoosePlanButtonClicked={onChoosePlanButtonClicked}
                type={type}
                isCurrentPlan={isCurrentPlan}
                enableNewPlanCardUI={enableNewPlanCardUI}
                isExpandedGrid={isExpandedGrid}
              />
            )
          })}
        </CardGroup>
        {enableNewBillingPlan && <EnterprisePlanDetail isExpandedGrid={isExpandedGrid} />}
      </Modal.Content>
    </Modal>
  )
}

/**
 * @params {object} props
 * @params {object} props.plan
 * @params {boolean} props.buttonDisabled
 * @params {function} props.onChoosePlanButtonClicked
 * @params {string} props.type
 * @params {boolean} props.isCurrentPlan
 * @params {boolean} props.enableNewPlanCardUI
 * @params {boolean} props.isExpandedGrid
 */
function PlanCard({
  plan,
  buttonDisabled,
  onChoosePlanButtonClicked,
  type,
  isCurrentPlan,
  enableNewPlanCardUI,
  isExpandedGrid,
}) {
  return (
    <React.Fragment>
      <Card key={plan.id} link={!buttonDisabled} onClick={onChoosePlanButtonClicked} as="div">
        <CardContent>
          <h1 className="center aligned">{plan.nickname}</h1>
          <div className="ui divider horizontal">{`$${monthlyAmount(
            type,
            priceAmount(plan)
          )} / month`}</div>
          {enableNewPlanCardUI ? (
            <PlanDetails2026
              isLegacyPlan={isLegacyPlan(plan)}
              planMetadata={plan.metadata}
              planNickname={plan.nickname}
            />
          ) : (
            <PlanDetailsV2 isLegacyPlan={isLegacyPlan(plan)} planNickname={plan.nickname} />
          )}
        </CardContent>
        <CardContent extra>
          {isCurrentPlan ? (
            <Button fluid disabled>
              Current Plan
            </Button>
          ) : (
            <Button fluid primary={!isExpandedGrid} disabled={buttonDisabled}>
              Choose {plan.nickname}
            </Button>
          )}
        </CardContent>
      </Card>
    </React.Fragment>
  )
}

function PlanDetailsV2({ isLegacyPlan, planNickname }) {
  const legacyItems = [
    {
      icon: 'green fas fa-check',
      content: 'Unlimited surveys',
    },
  ]
  let items

  if (!isLegacyPlan && planNickname.toLowerCase().includes('standard')) {
    items = [
      {
        icon: 'green fas fa-check',
        content: '5 users',
      },
      {
        icon: 'green fas fa-check',
        content: '1k monthly responses',
      },
      {
        icon: 'green fas fa-check',
        content: 'Unlimited surveys',
      },
      {
        icon: 'green fas fa-check',
        content: '50+ integrations',
      },
    ]
  } else if (!isLegacyPlan && planNickname.toLowerCase().includes('pro')) {
    items = [
      {
        icon: 'green fas fa-check',
        content: '15 users',
      },
      {
        icon: 'green fas fa-check',
        content: '3k monthly responses',
      },
      {
        icon: 'green fas fa-check',
        content: 'Multi-brand support',
      },
      {
        icon: 'green fas fa-check',
        content: 'Question visibility rules',
      },
    ]
  } else if (!isLegacyPlan && planNickname.toLowerCase().includes('elite')) {
    items = [
      {
        icon: 'green fas fa-check',
        content: '30 users',
      },
      {
        icon: 'green fas fa-check',
        content: '7k monthly responses',
      },
      {
        icon: 'green fas fa-check',
        content: 'Role-based access',
      },
      {
        icon: 'green fas fa-check',
        content: 'Custom email templates',
      },
    ]
  } else {
    items = legacyItems
  }

  return <List items={items} />
}

const PLAN_2026_FEATURES = [
  ['starter', ['Dashboards and reports', 'Unlimited surveys']],
  ['pro', ['Automatic topic detection', 'Branded survey URLs']],
  ['premium', ['Multi-language surveys', 'Custom topics']],
  ['scale', ['Enterprise SSO', 'Dedicated Slack channel']],
]

function planLimitContent(rawValue, singularNoun, pluralNoun) {
  const amount = Number(rawValue)
  if (!rawValue || !Number.isFinite(amount)) {
    return null
  }

  return `${amount.toLocaleString('en-US')} ${amount === 1 ? singularNoun : pluralNoun}`
}

function planFeatures(nickname) {
  const matched = PLAN_2026_FEATURES.find(([planKey]) => nickname.includes(planKey))

  return matched ? matched[1] : []
}

function PlanDetails2026({ isLegacyPlan, planMetadata, planNickname }) {
  if (isLegacyPlan) {
    return <List items={[{ icon: 'green fas fa-check', content: 'Unlimited surveys' }]} />
  }

  const limitLines = [
    planLimitContent(
      R.prop('monthly_response_limit', planMetadata),
      'monthly response',
      'monthly responses'
    ),
    planLimitContent(R.prop('users', planMetadata), 'account user', 'account users'),
    planLimitContent(R.prop('brands', planMetadata), 'brand', 'brands'),
  ].filter(Boolean)
  const nickname = String(planNickname ?? '').toLowerCase()
  const items = [...limitLines, ...planFeatures(nickname)].map((content) => ({
    icon: 'green fas fa-check',
    content,
  }))

  return <List items={items} />
}

function EnterprisePlanDetail({ isExpandedGrid }) {
  return (
    <Card fluid as="div" className={isExpandedGrid ? undefined : '!max-w-[900px]'}>
      <CardContent>
        <div className="flex flex-row flex-wrap items-center">
          <i className="!ml-2.5 !mr-6 big far fa-building icon" />
          <div className="flex-2">
            <h2 className="left aligned mb-0.5">Learn about our Enterprise plan</h2>
            <p>Designed for large organizations needing tailored solutions and personal service.</p>
          </div>
          <div>
            <a
              className="ml-auto"
              href="https://www.simplesat.io/contact-sales/"
              target="_blank"
              rel="noreferrer"
            >
              <button className="ui primary button">Contact us</button>
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/Billing/planDetailModal.js